import { useCallback } from 'react';
import seoTasksService from '@/services/seoTasks';
import seoOpportunitiesService from '@/services/seoOpportunities';
import { SeoKeywordOpportunity, OpportunityStatus, AiSuggestion } from '@/types/api/seo';

const suggestionToTaskType: Record<string, string> = {
  title_tag: 'title_tag',
  meta_description: 'meta_description',
  h2_section: 'content',
  paragraph: 'content',
  faq_entry: 'content',
  schema_markup: 'schema',
  internal_link: 'internal_link',
  redirect: 'other',
  custom: 'other',
};

export function useTaskCreation(
  projectId: string,
  keywordId: string,
  opportunities: SeoKeywordOpportunity[],
  setOpportunities: React.Dispatch<React.SetStateAction<SeoKeywordOpportunity[]>>,
  onTaskCreated?: () => void
) {
  const handleAddTask = useCallback(
    async (suggestion: AiSuggestion, opportunityId: string): Promise<boolean> => {
      try {
        await seoTasksService.create({
          projectId,
          keywordId,
          opportunityId,
          title: suggestion.label,
          description: suggestion.reasoning,
          taskType: (suggestionToTaskType[suggestion.type] ?? 'other') as any,
          suggestedValue: suggestion.value,
          priority: 'medium',
        });

        onTaskCreated?.();

        // Auto-move opportunity to in_progress when first task is created
        const opp = opportunities.find((o) => o.id === opportunityId);
        if (opp && opp.status === 'open') {
          await seoOpportunitiesService.updateStatus(opportunityId, 'in_progress');
          setOpportunities((prev) =>
            prev.map((o) => (o.id === opportunityId ? { ...o, status: 'in_progress' as OpportunityStatus } : o))
          );
        }

        return true;
      } catch {
        return false;
      }
    },
    [projectId, keywordId, opportunities, setOpportunities, onTaskCreated]
  );

  return { handleAddTask };
}
